import { useCollapseModel } from '@/store/collapse-models/collapse-model';
import { useEditorModel } from '@/store/models/editor-model';
import { usePluginModel } from '@/store/models/plugin-model';
import { useSdkModel } from '@/store/models/sdk-model';
// prettier-ignore 改文件包含样式初始化，需要优先加载，否则样式会互相覆盖
import './index.less';
import React, { useEffect } from 'react';
import { OpenPluginType, type IBackgroundLayerProps } from '@meitu/saas-editor-core/es/version-types';
import { Layout, LayoutOption, OptionType } from '@meitu/saas-editor-ui/es/layout';
import Collapse from '@/editor/collapse';
import useOpenPlugins, { AI_GENERATE_IMAGE_enter } from '@/editor/collapse/views/more-collapse/hooks/use-open-plugins';
import RenderAIExpendImage from '@/editor/collapse/views/more-collapse/plugins/ai-expend-image/expend-operate/render-ai-expend-image';
import RenderAiModifyImage from '@/editor/collapse/views/more-collapse/plugins/ai-modify-image/operate/render-ai-modify-image';
import OpenImageModal from '@/editor/collapse/views/more-collapse/plugins/open-image-modal/open-image-modal';
import RenderMattingImagePlugin from '@/editor/components/render-matting-image-plugin';
import { StageToolbar } from '@/editor/components/stage-toolbar';
import ExtensionContainer from '@/editor/extensions/components/extension-container/extension-container';
import Header from '@/editor/header';
import Menu from '@/editor/menu';
import Panel from '@/editor/panel';
import { useCommonLogic, useFontMaterials, useProgressHelper } from '@/hooks';
import { editor } from '@/instance';
import './driver.less';
import { type ICustomNodeData } from '@meitu/saas-editor-core/es/framework';
import DynamicBgLayer from '@meitu/saas-editor-ui/es/widgets/dynamic-bg-layer/dynamic-bg-layer';
import AIPPTComponentDemo from '@/editor/components/ai-ppt-template-demo/ai-ppt-component-demo';
import AIPPTTemplateDemo from '@/editor/components/ai-ppt-template-demo/aippt-template-demo';
import { events, INIT_EVENT_FINISH } from '@/events-bus';
import useFetchTemplate from '@/hooks/fetch-template';
import {
    isPPTDemoAndShowComponentList,
    isPPTDemoAndShowTemplateComponentList,
} from '@/hooks/fetch-template/editor-mode';

/** 添加舞台工具栏 */
editor.stageToolbar.addNode(<StageToolbar />);

const AppLogic = React.memo(() => {
    useCommonLogic();

    const { openPlugin } = useOpenPlugins();
    const customParams = useSdkModel((state) => state.customParams);
    const fetchTemplate = useFetchTemplate();
    const { loadFontMaterials } = useFontMaterials();
    // 顶部进度条
    const progressHelper = useProgressHelper();

    // 注册「打开插件」时间
    useEffect(() => {
        return editor.shortcutMenu.onOpenPlugin((plugin: OpenPluginType) => {
            openPlugin(plugin);
        });
    }, []);

    // 注册「进入 AI 生图」事件
    useEffect(() => {
        return editor.shortcutMenu.onAiGenerateImageEnter((entrance: string) => {
            AI_GENERATE_IMAGE_enter(entrance);
        });
    }, []);

    // 设置是否显示高级(AI)操作
    useEffect(() => {
        editor.shortcutMenu.setAdvancedAiEnabled(!customParams.hideAiText);
    }, [customParams.hideAiText]);

    // 先 mount/create，触发 Service.onCreate（含 TextEngine WASM 路径），再初始化字体与模板
    useEffect(() => {
        editor.mount();
    }, []);

    useEffect(() => {
        const init = async () => {
            progressHelper.start();
            try {
                // 确保 polyfill 完成
                await editor.textLayer.polyfill();

                // 初始化字体素材与 TextEngine（必须在 mount/create 之后）
                await loadFontMaterials();

                // 获取模版数据
                await fetchTemplate();

                // 设计师编辑器开启网格线插件
                editor.stageGrid.showGridLineOperate(true);
            } catch (error) {
                console.error('初始化失败', error);
            } finally {
                progressHelper.done();
            }
        };
        void init().then(() => {
            void editor.wasm.init({
                async success() {
                    await editor.textLayer.loadShapeTextAssets();
                },
            });
        });
    }, []);

    // 按当前渲染模式为可见页面的动态背景注册 DOM 层。
    useEffect(() => {
        const dynamicBgLayers = new Map<number, ICustomNodeData>();
        let syncTimer: ReturnType<typeof setTimeout> | null = null;

        const checkHasVisibleDynamicBg = (frameId: number) => {
            const backgroundLayer = editor.services.frame.getBackgroundLayer(frameId);
            const dynamicInfo = (backgroundLayer?.data as IBackgroundLayerProps | undefined)?.dynamicInfo;
            return !!dynamicInfo && !dynamicInfo.hidden;
        };

        const syncDynamicBgLayers = (frameIds: number[] = editor.services.frame.getRenderFrameIds()) => {
            const visibleFrameIds = editor.services.edgelessCanvas.isSingleMode
                ? frameIds.filter((frameId) => frameId === editor.services.frame.getActiveFrameId())
                : frameIds;
            const nextFrameIds = new Set(visibleFrameIds.filter(checkHasVisibleDynamicBg));
            const registeredNodes = new Set(editor.customLayer.getNodes());
            dynamicBgLayers.forEach((node, frameId) => {
                if (!nextFrameIds.has(frameId) || !registeredNodes.has(node)) {
                    editor.customLayer.remove(node);
                    dynamicBgLayers.delete(frameId);
                }
            });

            nextFrameIds.forEach((frameId) => {
                if (dynamicBgLayers.has(frameId)) return;
                const node = editor.customLayer.add(
                    frameId,
                    `dynamicBgLayer-${frameId}`,
                    <DynamicBgLayer editor={editor} frameId={frameId} />,
                    'bottom',
                );
                dynamicBgLayers.set(frameId, node);
            });
        };

        const initHandler = events.on(INIT_EVENT_FINISH, () => {
            if (syncTimer) clearTimeout(syncTimer);
            syncTimer = setTimeout(() => syncDynamicBgLayers(), 0);
        });
        const frameHandler = editor.services.frame.onRerenderAll((frameIds: number[]) => syncDynamicBgLayers(frameIds));
        const activeHandler = editor.services.frame.onActiveIndexChange(() => syncDynamicBgLayers());
        const modeHandler = editor.services.edgelessCanvas.onRenderModeChange(() => syncDynamicBgLayers());
        const layoutHandler = editor.services.frame.onFrameLayoutComplete(() => {
            // CustomLayer 的定位来自注册时的 Frame 形状，布局完成后重新注册以更新偏移。
            dynamicBgLayers.forEach((node) => editor.customLayer.remove(node));
            dynamicBgLayers.clear();
            syncDynamicBgLayers();
        });
        const layerHandler = editor.services.flatLayer.onChange((layer, _, args) => {
            if (layer && !editor.services.flatLayer.checkIsBackground(layer)) return;
            if (!layer && !args.isDelete) return;
            syncDynamicBgLayers();
        });
        syncDynamicBgLayers();

        return () => {
            if (syncTimer) clearTimeout(syncTimer);
            editor.off(frameHandler);
            editor.off(activeHandler);
            editor.off(modeHandler);
            editor.off(layoutHandler);
            editor.off(layerHandler);
            events.off(initHandler);
            dynamicBgLayers.forEach((node) => editor.customLayer.remove(node));
            dynamicBgLayers.clear();
        };
    }, []);

    return null;
});

const PosterEditor = () => {
    const currentType = useCollapseModel((state) => state.currentType);
    const isShowCollapse = useEditorModel((state) => state.isShowCollapse);
    const setIsShowCollapse = useEditorModel((state) => state.setIsShowCollapse);
    const isShowPanel = useEditorModel((state) => state.isShowPanel);

    const openImageModalType = usePluginModel((state) => state.openImageModalType);
    const isMatting = usePluginModel((state) => state.isMatting);
    const isAiExpendImage = usePluginModel((state) => state.isAiExpendImage);
    const isAiModifyImage = usePluginModel((state) => state.isAiModifyImage);
    return (
        <>
            {isPPTDemoAndShowTemplateComponentList() ? <AIPPTTemplateDemo editor={editor} /> : null}
            {isPPTDemoAndShowComponentList() ? <AIPPTComponentDemo editor={editor} /> : null}
            <Layout editor={editor} style={{ '--menu-width': '66px' } as React.CSSProperties}>
                <LayoutOption type={OptionType.header}>
                    <Header />
                </LayoutOption>
                <LayoutOption type={OptionType.menu}>
                    <Menu />
                </LayoutOption>
                {isPPTDemoAndShowTemplateComponentList() || isPPTDemoAndShowComponentList() ? null : (
                    <LayoutOption
                        isShowCollapse={isShowCollapse}
                        currentCollapse={currentType as string}
                        type={OptionType.collapse}
                    >
                        <Collapse />
                    </LayoutOption>
                )}
                <LayoutOption
                    isShowCollapse={isShowCollapse}
                    onClick={() => {
                        setIsShowCollapse(!isShowCollapse);
                    }}
                    onResize={(width, height) => {
                        editor.setViewport(width || 1, height || 1);
                    }}
                    type={OptionType.stage}
                >
                    {/* 编辑器容器 */}
                    <div id="editor-container" style={{ position: 'fixed' }}></div>

                    {/* 插件容器 */}
                    <ExtensionContainer useVisibility />
                </LayoutOption>
                <LayoutOption isShowPanel={isShowPanel} type={OptionType.panel}>
                    <Panel />
                </LayoutOption>
                <LayoutOption type={OptionType.extra}></LayoutOption>
                <LayoutOption type={OptionType.cols1}>
                    {/* 单列 */}
                    {/* {isPPT ? <MaterialShortOpera /> : null} */}
                </LayoutOption>
                <LayoutOption type={OptionType.cols2}>
                    {/* 双列 */}
                    {isAiExpendImage ? <RenderAIExpendImage /> : null}
                    {isAiModifyImage ? <RenderAiModifyImage /> : null}
                    {isMatting ? <RenderMattingImagePlugin /> : null}
                </LayoutOption>
                <LayoutOption type={OptionType.cols3}>
                    {/* 三列 */}
                    {openImageModalType ? <OpenImageModal /> : null}
                </LayoutOption>
            </Layout>
            {/* 通用组件 */}
            <CommonComponents />
        </>
    );
};

/** 通用组件 */
const CommonComponents = () => {
    return (
        <>
            <AppLogic />
        </>
    );
};

export default PosterEditor;
